'use client';
import React, { useState, useRef, useCallback } from 'react';

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function audioBufferToWav(buffer: AudioBuffer): Blob {
  const numChannels = buffer.numberOfChannels;
  const sampleRate = buffer.sampleRate;
  const length = buffer.length * numChannels * 2;
  const ab = new ArrayBuffer(44 + length);
  const view = new DataView(ab);
  const ws = (o: number, s: string) => { for (let i = 0; i < s.length; i++) view.setUint8(o + i, s.charCodeAt(i)); };
  ws(0, 'RIFF'); view.setUint32(4, 36 + length, true); ws(8, 'WAVE'); ws(12, 'fmt ');
  view.setUint32(16, 16, true); view.setUint16(20, 1, true); view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true); view.setUint32(28, sampleRate * numChannels * 2, true);
  view.setUint16(32, numChannels * 2, true); view.setUint16(34, 16, true); ws(36, 'data');
  view.setUint32(40, length, true);
  let offset = 44;
  for (let i = 0; i < buffer.length; i++) {
    for (let ch = 0; ch < numChannels; ch++) {
      const s = Math.max(-1, Math.min(1, buffer.getChannelData(ch)[i]));
      view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
      offset += 2;
    }
  }
  return new Blob([ab], { type: 'audio/wav' });
}

export default function FadeAudioTool() {
  const [audioFile, setAudioFile] = useState<{ file: File; url: string; size: number; duration: number } | null>(null);
  const [fadeInDuration, setFadeInDuration] = useState(2);
  const [fadeOutDuration, setFadeOutDuration] = useState(2);
  const [enableFadeIn, setEnableFadeIn] = useState(true);
  const [enableFadeOut, setEnableFadeOut] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [resultUrl, setResultUrl] = useState<string | null>(null);
  const [resultSize, setResultSize] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadFile = useCallback((file: File) => {
    if (file.size > 150 * 1024 * 1024) { setError('File too large. Max 150 MB.'); return; }
    setError(null); setResultUrl(null);
    const url = URL.createObjectURL(file);
    const audio = new Audio(url);
    audio.onloadedmetadata = () => setAudioFile({ file, url, size: file.size, duration: audio.duration });
    audio.onerror = () => setError('Could not read audio file.');
  }, []);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault(); setIsDragging(false);
    const f = e.dataTransfer.files?.[0]; if (f) loadFile(f);
  };

  const handleProcess = async () => {
    if (!audioFile) return;
    setProcessing(true); setProgress(10); setError(null); setResultUrl(null);
    try {
      const AudioCtx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof window.AudioContext }).webkitAudioContext;
      if (!AudioCtx) { setError('Web Audio API not supported.'); setProcessing(false); return; }
      setProgress(20);
      const arrayBuffer = await audioFile.file.arrayBuffer();
      setProgress(35);
      const audioCtx = new AudioCtx();
      const decoded = await audioCtx.decodeAudioData(arrayBuffer);
      await audioCtx.close();
      setProgress(50);

      const offCtx = new OfflineAudioContext(decoded.numberOfChannels, decoded.length, decoded.sampleRate);
      const source = offCtx.createBufferSource();
      source.buffer = decoded;
      const gainNode = offCtx.createGain();
      gainNode.gain.value = 1;

      if (enableFadeIn && fadeInDuration > 0) {
        gainNode.gain.setValueAtTime(0, 0);
        gainNode.gain.linearRampToValueAtTime(1, Math.min(fadeInDuration, decoded.duration));
      }
      if (enableFadeOut && fadeOutDuration > 0) {
        const fadeOutStart = Math.max(0, decoded.duration - fadeOutDuration);
        gainNode.gain.setValueAtTime(1, fadeOutStart);
        gainNode.gain.linearRampToValueAtTime(0, decoded.duration);
      }

      source.connect(gainNode);
      gainNode.connect(offCtx.destination);
      source.start(0);
      setProgress(65);
      const rendered = await offCtx.startRendering();
      setProgress(85);
      const blob = audioBufferToWav(rendered);
      setResultUrl(URL.createObjectURL(blob));
      setResultSize(blob.size);
      setProgress(100);
    } catch (err) {
      console.error(err);
      setError('Processing failed. Try a WAV or MP3 file under 150 MB.');
    } finally {
      setProcessing(false);
    }
  };

  const handleDownload = () => {
    if (!resultUrl || !audioFile) return;
    const a = document.createElement('a');
    a.href = resultUrl;
    a.download = `faded_${audioFile.file.name.replace(/\.[^.]+$/, '')}.wav`;
    a.click();
  };

  return (
    <div className="space-y-5">
      {error && (
        <div className="flex items-start gap-3 bg-red-50 border border-red-100 rounded-xl p-4">
          <svg className="w-5 h-5 text-red-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
          </svg>
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}
      {!audioFile ? (
        <div
          className={`rounded-2xl border-2 border-dashed p-10 text-center cursor-pointer transition-colors ${isDragging ? 'border-amber-400 bg-amber-50' : 'border-gray-200 bg-gray-50 hover:border-amber-300 hover:bg-amber-50/50'}`}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
        >
          <span className="text-4xl block mb-3">🌅</span>
          <p className="font-semibold text-gray-800 mb-1">Upload Audio File</p>
          <p className="text-gray-500 text-sm mb-3">Drag & drop or click to browse</p>
          <p className="text-gray-400 text-xs">Supports MP3, WAV, OGG · Max 150 MB</p>
          <input ref={fileInputRef} type="file" accept=".mp3,.wav,.ogg,.m4a,.aac" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) loadFile(f); }} />
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center gap-3 bg-amber-50 border border-amber-100 rounded-xl p-4">
            <span className="text-2xl">🌅</span>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-gray-800 text-sm truncate">{audioFile.file.name}</p>
              <p className="text-gray-500 text-xs">{formatBytes(audioFile.size)}</p>
            </div>
            <button onClick={() => { setAudioFile(null); setResultUrl(null); }} className="text-gray-400 hover:text-red-500 p-1">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
          <div className="bg-gray-50 rounded-xl p-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Preview</p>
            <audio src={audioFile.url} controls className="w-full h-10" />
          </div>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-gray-700">Fade In</p>
                <p className="text-xs text-gray-400">Gradually increase volume at start</p>
              </div>
              <button onClick={() => setEnableFadeIn(!enableFadeIn)} className={`w-12 h-6 rounded-full transition-colors ${enableFadeIn ? 'bg-amber-500' : 'bg-gray-200'}`}>
                <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform mx-0.5 ${enableFadeIn ? 'translate-x-6' : 'translate-x-0'}`} />
              </button>
            </div>
            {enableFadeIn && (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs text-gray-600">Fade In Duration</label>
                  <span className="text-amber-600 font-bold text-xs">{fadeInDuration}s</span>
                </div>
                <input type="range" min={0.5} max={10} step={0.5} value={fadeInDuration} onChange={(e) => { setFadeInDuration(Number(e.target.value)); setResultUrl(null); }} className="w-full accent-amber-500" />
              </div>
            )}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-gray-700">Fade Out</p>
                <p className="text-xs text-gray-400">Gradually decrease volume at end</p>
              </div>
              <button onClick={() => setEnableFadeOut(!enableFadeOut)} className={`w-12 h-6 rounded-full transition-colors ${enableFadeOut ? 'bg-amber-500' : 'bg-gray-200'}`}>
                <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform mx-0.5 ${enableFadeOut ? 'translate-x-6' : 'translate-x-0'}`} />
              </button>
            </div>
            {enableFadeOut && (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs text-gray-600">Fade Out Duration</label>
                  <span className="text-amber-600 font-bold text-xs">{fadeOutDuration}s</span>
                </div>
                <input type="range" min={0.5} max={10} step={0.5} value={fadeOutDuration} onChange={(e) => { setFadeOutDuration(Number(e.target.value)); setResultUrl(null); }} className="w-full accent-amber-500" />
              </div>
            )}
          </div>
          {processing && (
            <div>
              <div className="flex justify-between text-xs text-gray-500 mb-1"><span>Applying fade effects…</span><span>{progress}%</span></div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden"><div className="h-full bg-gradient-to-r from-amber-400 to-amber-600 rounded-full transition-all duration-300" style={{ width: `${progress}%` }} /></div>
            </div>
          )}
          {resultUrl && (
            <div className="bg-green-50 border border-green-100 rounded-xl p-4 space-y-3">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                <p className="font-semibold text-green-800 text-sm">Fade applied! · {formatBytes(resultSize)}</p>
              </div>
              <audio src={resultUrl} controls className="w-full h-10" />
              <button onClick={handleDownload} className="w-full py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-xl transition-colors text-sm">⬇️ Download Audio (WAV)</button>
            </div>
          )}
          {!resultUrl && (
            <button onClick={handleProcess} disabled={processing || (!enableFadeIn && !enableFadeOut)} className="w-full py-3.5 bg-amber-500 hover:bg-amber-600 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-colors text-sm">
              {processing ? 'Processing…' : 'Apply Fade Effects'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
